import { createSelector } from "@reduxjs/toolkit";

import { RootState } from "app/store";

import { ColorScheme } from "../types/ColorScheme";
import { UserConfiguration } from "types/AppConfig";
import { selectUserConfiguration } from "features/config/store/selectors";

const selectUserSettings = (state: RootState) => state.user.usersettings;

const selectIsDirtyUserSettings = (state: RootState) => state.user.isDirty;
const selectIsSavingUserSettings = (state: RootState) => state.user.isSaving;
const selectSessionId = (state: RootState) => state.user.sessionId;

const selectHasLoadedUserSettings = (state: RootState) =>
    state.user.usersettings !== undefined;

const selectColorScheme = (state: RootState) =>
    selectUserSettings(state)?.colorScheme ?? ColorScheme.System;
const selectAudioEnabledByDefault = (state: RootState) =>
    selectUserSettings(state)?.audioEnabledByDefault ?? false;

const selectGridViewMaxNumberOfDevicesPerGridRow = (state: RootState) =>
    selectUserSettings(state)?.gridViewMaxNumberOfDevicesPerGridRow ?? 3;
const selectGridViewEnableAutoSizing = (state: RootState) =>
    selectUserSettings(state)?.gridViewEnableAutoSizing ?? false;

const selectShowUnrestrictedFeedsByDefault = (state: RootState) =>
    selectUserSettings(state)?.showUnrestrictedFeedsByDefault ?? false;

const selectDebugToolsShowBufferingRecordingsByDefault = (state: RootState) =>
    selectUserSettings(state)?.debugToolsShowBufferingRecordingsByDefault ??
    false;
const selectDebugToolsShowForwardAndReversedRecordingsByDefault = (
    state: RootState,
) =>
    selectUserSettings(state)
        ?.debugToolsShowForwardAndReversedRecordingsByDefault ?? false;
const selectDebugToolsShowVideoDirectionInformationByDefault = (
    state: RootState,
) =>
    selectUserSettings(state)
        ?.debugToolsShowVideoDirectionInformationByDefault ?? false;
const selectDebugToolsShowRecordingsOutsidePlaybackAndBufferingWindowByDefault =
    (state: RootState) =>
        selectUserSettings(state)
            ?.debugToolsShowRecordingsOutsidePlaybackAndBufferingWindowByDefault ??
        false;
const selectDebugToolsShowStatisticsByDefault = (state: RootState) =>
    selectUserSettings(state)?.debugToolsShowStatisticsByDefault ?? false;
const selectDebugToolsShowSystemControlsByDefault = (state: RootState) =>
    selectUserSettings(state)?.debugToolsShowSystemControlsByDefault ?? false;

const selectShowJobRunsDialog = (state: RootState) =>
    state.user.showJobRunsDialog;

const selectUserSettingsRefreshIntervalInSeconds = createSelector(
    [selectUserConfiguration],
    (userConfiguration?: UserConfiguration) =>
        userConfiguration?.userSettingsRefreshIntervalInSeconds ?? 0,
);

export const userSelectors = {
    selectUserSettings,
    selectIsDirtyUserSettings,
    selectIsSavingUserSettings,
    selectHasLoadedUserSettings,
    selectColorScheme,
    selectAudioEnabledByDefault,
    selectGridViewEnableAutoSizing,
    selectGridViewMaxNumberOfDevicesPerGridRow,
    selectShowUnrestrictedFeedsByDefault,
    selectDebugToolsShowBufferingRecordingsByDefault,
    selectDebugToolsShowForwardAndReversedRecordingsByDefault,
    selectDebugToolsShowVideoDirectionInformationByDefault,
    selectDebugToolsShowRecordingsOutsidePlaybackAndBufferingWindowByDefault,
    selectDebugToolsShowStatisticsByDefault,
    selectDebugToolsShowSystemControlsByDefault,
    selectUserSettingsRefreshIntervalInSeconds,
    selectSessionId,
    selectShowJobRunsDialog,
};
